import axios from 'axios';
import { toast } from 'sonner';
import i18n from '@/i18n';

const _envBackendUrlRaw = process.env.REACT_APP_BACKEND_URL;
const _envBackendUrl = (_envBackendUrlRaw || '').trim().replace(/\/+$/, '');
const _localBackendUrl = 'http://localhost:8000';
const _liveBackendUrl = 'https://spartansapi.mc1services.com';

const _isBrowser = typeof window !== 'undefined' && window.location && typeof window.location.hostname === 'string';
const _hostname = _isBrowser ? window.location.hostname.trim().toLowerCase() : '';

let BACKEND_URL = _envBackendUrl || '';

// Production site hostname should always use the live backend.
if (_hostname === 'spartans.mc1services.com') {
  BACKEND_URL = _liveBackendUrl;
} else if (_hostname === 'localhost' || _hostname === '127.0.0.1') {
  // In local dev, allow overriding the backend via REACT_APP_BACKEND_URL.
  BACKEND_URL = _envBackendUrl || _localBackendUrl;
}

const API_BASE = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

if (process.env.NODE_ENV !== 'production' && typeof console !== 'undefined' && typeof console.log === 'function') {
  console.log('[api] base', API_BASE);
}

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const langRaw = i18n?.language || '';
  const lang = (typeof langRaw === 'string' ? langRaw : '').trim().toLowerCase().split('-')[0];
  if (lang) {
    config.headers['Accept-Language'] = lang;
  }
  return config;
});

let lastPermissionToastAt = 0;

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    if (status === 403) {
      const now = Date.now();
      if (now - lastPermissionToastAt > 2000) {
        toast.error('Permission not granted to this functionality.');
        lastPermissionToastAt = now;
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  processGoogleSession: (sessionId) => api.get(`/auth/session?session_id=${sessionId}`),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
};

// Locations
export const locationAPI = {
  list: () => api.get('/locations'),
  listAll: () => api.get('/locations/all/include-deleted'),
  get: (id) => api.get(`/locations/${id}`),
  create: (data) => api.post('/locations', data),
  update: (id, data) => api.patch(`/locations/${id}`, data),
  delete: (id) => api.delete(`/locations/${id}`),
  restore: (id) => api.post(`/locations/${id}/restore`),
  getUsers: (id) => api.get(`/locations/${id}/users`),
  getOverview: (id) => api.get(`/locations/${id}/overview`),
};

// Dashboard
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
};

// Analytics
export const analyticsAPI = {
  getOwnerDashboard: () => api.get('/analytics/owner-dashboard'),
};

// Categories
export const categoryAPI = {
  list: (locationId, params = {}) => api.get('/program-categories', { params: { ...(locationId ? { location_id: locationId } : {}), ...params } }),
  create: (data) => api.post('/program-categories', data),
  update: (id, data) => api.patch(`/program-categories/${id}`, data),
  delete: (id) => api.delete(`/program-categories/${id}`),
  restore: (id) => api.post(`/program-categories/${id}/restore`),
};

// Programs
export const programAPI = {
  list: (locationId, categoryId, params = {}) => api.get('/programs', { params: { ...(locationId && { location_id: locationId }), ...(categoryId && { category_id: categoryId }), ...params } }),
  get: (id, locationId) => api.get(`/programs/${id}`, { params: locationId ? { location_id: locationId } : {} }),
  create: (data) => api.post('/programs', data),
  update: (id, data) => api.patch(`/programs/${id}`, data),
  delete: (id) => api.delete(`/programs/${id}`),
  restore: (id) => api.post(`/programs/${id}/restore`),
};

// Instructors
export const instructorAPI = {
  list: (locationId, params = {}) => api.get('/instructors', { params: { ...(locationId ? { location_id: locationId } : {}), ...params } }),
  create: (data) => api.post('/instructors', data),
  update: (id, data) => api.patch(`/instructors/${id}`, data),
  delete: (id) => api.delete(`/instructors/${id}`),
  restore: (id) => api.post(`/instructors/${id}/restore`),
};

// Schedules
export const scheduleAPI = {
  list: (locationId, filters = {}) => api.get('/class-schedules', { params: { ...(locationId && { location_id: locationId }), ...filters } }),
  create: (data) => api.post('/class-schedules', data),
  update: (id, data) => api.patch(`/class-schedules/${id}`, data),
  delete: (id) => api.delete(`/class-schedules/${id}`),
  restore: (id) => api.post(`/class-schedules/${id}/restore`),
  generateSessions: (id, weeks = 8) => api.post(`/class-schedules/${id}/generate-sessions`, null, { params: { weeks } }),
};

// Sessions
export const sessionAPI = {
  list: (locationId, filters = {}) => api.get('/class-sessions', { params: { ...(locationId && { location_id: locationId }), ...filters } }),
  create: (data) => api.post('/class-sessions', data),
  update: (id, data) => api.patch(`/class-sessions/${id}`, data),
  delete: (id) => api.delete(`/class-sessions/${id}`),
};

// Attendance
export const attendanceAPI = {
  save: (data) => api.post('/attendance', data),
  list: (locationId, filters = {}) => api.get('/attendance', { params: { ...(locationId && { location_id: locationId }), ...filters } }),
  summary: (locationId) => api.get('/attendance/summary', { params: locationId ? { location_id: locationId } : {} }),
};

// Manager
export const managerAPI = {
  getStats: () => api.get('/manager/stats'),
};

// Seed
export const seedAPI = {
  seed: () => api.post('/seed'),
  seedPrograms: () => api.post('/seed-programs'),
  seedBookings: () => api.post('/customer/seed-bookings'),
  seedHR: () => api.post('/seed-hr'),
};

// Customer APIs
export const customerAPI = {
  locations: () => api.get('/customer/locations'),
  dashboard: () => api.get('/customer/dashboard'),
  profile: () => api.get('/customer/me'),
  updateProfile: (data) => api.patch('/customer/me', data),
  switchLocation: (data) => api.patch('/customer/location', data),
  categories: () => api.get('/customer/program-categories'),
  programs: (categoryId) => api.get('/customer/programs', { params: categoryId ? { category_id: categoryId } : {} }),
  sessions: (startDate, endDate, programId) => api.get('/customer/class-sessions', {
    params: { ...(startDate && { start_date: startDate }), ...(endDate && { end_date: endDate }), ...(programId && { program_id: programId }) }
  }),
  createBooking: (data) => api.post('/customer/bookings', data),
  bookings: (filters = {}) => api.get('/customer/bookings', { params: filters }),
  upcomingBookings: () => api.get('/customer/bookings/upcoming'),
  cancelBooking: (id) => api.patch(`/customer/bookings/${id}/cancel`),
  attendance: (fromDate, toDate) => api.get('/customer/attendance', { params: { ...(fromDate && { from_date: fromDate }), ...(toDate && { to_date: toDate }) } }),
  attendanceSummary: () => api.get('/customer/attendance/summary'),
};

// Upload
export const uploadAPI = {
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

// Staff
export const staffAPI = {
  list: (filters = {}) => api.get('/staff', { params: filters }),
  get: (id, locationId) => api.get(`/staff/${id}`, { params: { location_id: locationId } }),
  create: (data, locationId) => api.post('/staff', data, { params: { location_id: locationId } }),
  update: (id, data, locationId) => api.patch(`/staff/${id}`, data, { params: { location_id: locationId } }),
  me: () => api.get('/staff/me'),
  updateMe: (data) => api.patch('/staff/me', data),
  getMaster: (id, locationId) => api.get(`/staff/${id}/master`, { params: { location_id: locationId } }),
  updateEmploymentSetup: (id, data, locationId) => api.patch(`/staff/${id}/employment-setup`, data, { params: { location_id: locationId } }),
  listDocuments: (id, locationId) => api.get(`/staff/${id}/documents`, { params: { location_id: locationId } }),
  addDocument: (id, data, locationId) => api.post(`/staff/${id}/documents`, data, { params: { location_id: locationId } }),
  updateDocument: (id, documentId, data, locationId) => api.patch(`/staff/${id}/documents/${documentId}`, data, { params: { location_id: locationId } }),
  listAgreements: (id, locationId) => api.get(`/staff/${id}/agreements`, { params: { location_id: locationId } }),
  addAgreement: (id, data, locationId) => api.post(`/staff/${id}/agreements`, data, { params: { location_id: locationId } }),
  updateAgreement: (id, agreementId, data, locationId) => api.patch(`/staff/${id}/agreements/${agreementId}`, data, { params: { location_id: locationId } }),
  getProbation: (id, locationId) => api.get(`/staff/${id}/probation`, { params: { location_id: locationId } }),
  addProbationEvent: (id, data, locationId) => api.post(`/staff/${id}/probation/event`, data, { params: { location_id: locationId } }),
  listVisa: (id, locationId) => api.get(`/staff/${id}/visa`, { params: { location_id: locationId } }),
  addVisa: (id, data, locationId) => api.post(`/staff/${id}/visa`, data, { params: { location_id: locationId } }),
  updateVisa: (id, visaId, data, locationId) => api.patch(`/staff/${id}/visa/${visaId}`, data, { params: { location_id: locationId } }),
  listAssets: (id, locationId) => api.get(`/staff/${id}/assets`, { params: { location_id: locationId } }),
  addAsset: (id, data, locationId) => api.post(`/staff/${id}/assets`, data, { params: { location_id: locationId } }),
  listExitCases: (id, locationId) => api.get(`/staff/${id}/exit`, { params: { location_id: locationId } }),
  addExitCase: (id, data, locationId) => api.post(`/staff/${id}/exit`, data, { params: { location_id: locationId } }),
  listNotes: (id, locationId) => api.get(`/staff/${id}/notes`, { params: { location_id: locationId } }),
  addNote: (id, data, locationId) => api.post(`/staff/${id}/notes`, data, { params: { location_id: locationId } }),
  listEmploymentEvents: (id, locationId) => api.get(`/staff/${id}/employment-events`, { params: { location_id: locationId } }),
  addEmploymentEvent: (id, data, locationId) => api.post(`/staff/${id}/employment-event`, data, { params: { location_id: locationId } }),
  myRequests: () => api.get('/staff/me/requests'),
  createRequest: (data) => api.post('/staff/me/requests', data),
  myAttendance: (params = {}) => api.get('/staff/me/attendance', { params }),
  listAttendance: (id, params = {}) => api.get(`/staff/${id}/attendance`, { params }),
  addAttendance: (id, data, params = {}) => api.post(`/staff/${id}/attendance`, data, { params }),
  myPerformance: () => api.get('/staff/me/performance'),
  addPerformanceReport: (id, data, params = {}) => api.post(`/staff/${id}/performance/reports`, data, { params }),
  listPerformanceReports: (id, params = {}) => api.get(`/staff/${id}/performance/reports`, { params }),
  addWarning: (id, data, params = {}) => api.post(`/staff/${id}/performance/warnings`, data, { params }),
  listWarnings: (id, params = {}) => api.get(`/staff/${id}/performance/warnings`, { params }),
  addAward: (id, data, params = {}) => api.post(`/staff/${id}/performance/awards`, data, { params }),
  listAwards: (id, params = {}) => api.get(`/staff/${id}/performance/awards`, { params }),
  myNotifications: () => api.get('/staff/me/notifications'),
  notificationsInbox: (params = {}) => api.get('/staff/notifications/inbox', { params }),
  requestsInbox: (params = {}) => api.get('/staff/requests/inbox', { params }),
  actOnRequest: (requestId, data) => api.patch(`/staff/requests/${requestId}`, data),
  transfer: (id, data, locationId) => api.post(`/staff/${id}/transfer`, data, { params: { location_id: locationId } }),
  delete: (id, locationId) => api.delete(`/staff/${id}`, { params: { location_id: locationId } }),
  restore: (id, locationId) => api.post(`/staff/${id}/restore`, null, { params: { location_id: locationId } }),
  permanentDelete: (id, locationId) => api.delete(`/staff/${id}/permanent`, { params: { location_id: locationId } }),
};

// Payroll
export const payrollAPI = {
  list: (filters = {}) => api.get('/payroll', { params: filters }),
  get: (id, locationId) => api.get(`/payroll/${id}`, { params: { location_id: locationId } }),
  create: (data, locationId) => api.post('/payroll', data, { params: { location_id: locationId } }),
  update: (id, data, locationId) => api.patch(`/payroll/${id}`, data, { params: { location_id: locationId } }),
  updateStatus: (id, data, locationId) => api.patch(`/payroll/${id}/status`, data, { params: { location_id: locationId } }),
};

// Expenses
export const expenseAPI = {
  list: (filters = {}) => api.get('/expenses', { params: filters }),
  get: (id, locationId) => api.get(`/expenses/${id}`, { params: { location_id: locationId } }),
  create: (data, locationId) => api.post('/expenses', data, { params: { location_id: locationId } }),
  update: (id, data, locationId) => api.patch(`/expenses/${id}`, data, { params: { location_id: locationId } }),
  delete: (id, locationId) => api.delete(`/expenses/${id}`, { params: { location_id: locationId } }),
};

// Finance
export const financeAPI = {
  locationSummary: (periodStart, periodEnd, locationId) => api.get('/finance/location-summary', { params: { period_start: periodStart, period_end: periodEnd, location_id: locationId } }),
  ownerSummary: (periodStart, periodEnd) => api.get('/finance/owner-summary', { params: { period_start: periodStart, period_end: periodEnd } }),
  payrollByStaff: (periodStart, periodEnd, locationId) => api.get('/finance/payroll-by-staff', { params: { period_start: periodStart, period_end: periodEnd, location_id: locationId } }),
};

// HR
export const hrAPI = {
  ownerOverview: () => api.get('/hr/owner-overview'),
  owners: (params = {}) => api.get('/hr/owners', { params }),
};

// Public APIs (no auth required)
export const publicAPI = {
  getSiteSettings: () => api.get('/public/site-settings'),
  getBranches: () => api.get('/public/branches'),
  getBranch: (id) => api.get(`/public/branches/${id}`),
  getDisciplines: () => api.get('/public/disciplines'),
  getDiscipline: (slug) => api.get(`/public/disciplines/${slug}`),
  getPricing: () => api.get('/public/pricing'),
  getFaqs: (locationId = null) => api.get('/public/faqs', { params: locationId ? { location_id: locationId } : {} }),
  getPage: (slug) => api.get(`/public/pages/${slug}`),
  submitContact: (data) => api.post('/public/contact', data),
  getStats: () => api.get('/public/stats'),
  getGoogleReviews: () => api.get('/public/google-reviews'),
  getBranchGoogleReviews: (id) => api.get(`/public/branches/${id}/google-reviews`),
  getGallery: (params) => api.get('/public/gallery', { params }),
  getCategories: (locationId) => api.get('/public/categories', { params: { location_id: locationId } }),
  getPrograms: (locationId) => api.get('/public/programs', { params: { location_id: locationId } }),
  getSchedule: (locationId) => api.get('/public/schedule', { params: { location_id: locationId } }),
};

// User Management APIs (Owner only)
export const userManagementAPI = {
  list: (params = {}) => api.get('/admin/users', { params }),
  get: (id) => api.get(`/admin/users/${id}`),
  create: (data) => api.post('/admin/users', data),
  update: (id, data) => api.patch(`/admin/users/${id}`, data),
  resetPassword: (id, data) => api.patch(`/admin/users/${id}/reset-password`, data),
  deactivate: (id) => api.patch(`/admin/users/${id}/deactivate`),
  activate: (id) => api.patch(`/admin/users/${id}/activate`),
  delete: (id) => api.delete(`/admin/users/${id}`),
  restore: (id) => api.post(`/admin/users/${id}/restore`),
  permanentDelete: (id) => api.delete(`/admin/users/${id}/permanent`),
  getStats: () => api.get('/admin/users/stats/summary'),
  syncMembers: () => api.post('/admin/users/sync-members'),
};

// Roles & Permissions APIs (Owner only)
export const rolesAPI = {
  list: (params = {}) => api.get('/admin/roles', { params }),
  get: (id) => api.get(`/admin/roles/${id}`),
  create: (data) => api.post('/admin/roles', data),
  update: (id, data) => api.patch(`/admin/roles/${id}`, data),
  delete: (id) => api.delete(`/admin/roles/${id}`),
  getPermissions: () => api.get('/admin/roles/permissions'),
  getUserPermissions: (userId) => api.get(`/admin/roles/user/${userId}/permissions`),
  getMyPermissions: () => api.get('/admin/roles/me/permissions'),
  seedRoles: () => api.post('/admin/roles/seed'),
};

// Members APIs
export const membersAPI = {
  list: (locationId, params = {}) => api.get('/members', { params: { location_id: locationId, ...params } }),
  get: (id, locationId) => api.get(`/members/${id}`, { params: { location_id: locationId } }),
  create: (data, locationId) => api.post('/members', data, { params: { location_id: locationId } }),
  update: (id, data, locationId) => api.patch(`/members/${id}`, data, { params: { location_id: locationId } }),
  activate: (id, locationId) => api.patch(`/members/${id}/activate`, null, { params: { location_id: locationId } }),
  deactivate: (id, locationId) => api.patch(`/members/${id}/deactivate`, null, { params: { location_id: locationId } }),
  delete: (id, locationId) => api.delete(`/members/${id}`, { params: { location_id: locationId } }),
  restore: (id, locationId) => api.post(`/members/${id}/restore`, null, { params: { location_id: locationId } }),
  signAgreement: (memberId, data, locationId) => api.post(`/members/${memberId}/sign-agreement`, data, { params: { location_id: locationId } }),
  getSignedAgreements: (memberId, locationId) => api.get(`/members/${memberId}/signed-agreements`, { params: { location_id: locationId } }),
  downloadSignedAgreement: (memberId, signedAgreementId, locationId) => api.get(`/members/${memberId}/signed-agreements/${signedAgreementId}/download`, { params: { location_id: locationId }, responseType: 'blob' }),
  getAcceptedTerms: (memberId, locationId) => api.get(`/members/${memberId}/accepted-terms`, { params: { location_id: locationId } }),
  getAcceptedTermsDoc: (memberId, acceptedTermsId, locationId) => api.get(`/members/${memberId}/accepted-terms/${acceptedTermsId}`, { params: { location_id: locationId } }),
  downloadAcceptedTerms: (memberId, acceptedTermsId, locationId) => api.get(`/members/${memberId}/accepted-terms/${acceptedTermsId}/download`, { params: { location_id: locationId }, responseType: 'blob' }),
  viewRegistrationDocumentPdf: (memberId, locationId) => api.get(`/members/${memberId}/registration-document/pdf`, { params: { location_id: locationId }, responseType: 'blob' }),
  downloadRegistrationDocumentPdf: (memberId, locationId) => api.get(`/members/${memberId}/registration-document/pdf/download`, { params: { location_id: locationId }, responseType: 'blob' }),
  requestTransfer: (memberId, data, locationId) => api.post(`/members/${memberId}/transfer`, data, { params: { location_id: locationId } }),
  listProfiles: () => api.get('/member/profiles'),
  acceptTerms: () => api.post('/member/accept-terms'),
  getPrimaryContact: () => api.get('/member/primary-contact'),
  createProfile: (data) => api.post('/member/profiles', data),
  setPortalPassword: (memberId, password, locationId) => api.patch(`/members/${memberId}/portal-password`, { password }, { params: { location_id: locationId } }),
  resetPortalPassword: (memberId, locationId) => api.post(`/members/${memberId}/portal-password/reset`, null, { params: { location_id: locationId } }),
};

// Memberships APIs
export const membershipsAPI = {
  list: (locationId, params = {}) => api.get('/memberships', { params: { location_id: locationId, ...params } }),
  get: (id, locationId) => api.get(`/memberships/${id}`, { params: { location_id: locationId } }),
  create: (memberId, data, locationId) => api.post(`/members/${memberId}/memberships`, data, { params: { location_id: locationId } }),
  update: (id, data, locationId) => api.patch(`/memberships/${id}`, data, { params: { location_id: locationId } }),
  activate: (id, locationId) => api.post(`/memberships/${id}/activate`, null, { params: { location_id: locationId } }),
  
  // Freezes
  listFreezes: (id, locationId) => api.get(`/memberships/${id}/freezes`, { params: { location_id: locationId } }),
  createFreeze: (id, data, locationId) => api.post(`/memberships/${id}/freezes`, data, { params: { location_id: locationId } }),
  updateFreeze: (id, freezeId, data, locationId) => api.patch(`/memberships/${id}/freezes/${freezeId}`, data, { params: { location_id: locationId } }),
  deleteFreeze: (id, freezeId, locationId) => api.delete(`/memberships/${id}/freezes/${freezeId}`, { params: { location_id: locationId } }),
};

// Payment Plans APIs
export const paymentPlansAPI = {
  list: (locationId, activeOnly = true, membershipType = null) => api.get('/payment-plans', { params: { location_id: locationId, active_only: activeOnly, membership_type: membershipType || undefined } }),
  create: (data, locationId) => api.post('/payment-plans', data, { params: { location_id: locationId } }),
  update: (id, data, locationId) => api.patch(`/payment-plans/${id}`, data, { params: { location_id: locationId } }),
  delete: (id, locationId) => api.delete(`/payment-plans/${id}`, { params: { location_id: locationId } }),
};

// Agreements APIs
export const agreementsAPI = {
  list: (locationId, activeOnly = true) => api.get('/agreements', { params: { location_id: locationId, active_only: activeOnly } }),
  create: (data, locationId) => api.post('/agreements', data, { params: { location_id: locationId } }),
  update: (id, data, locationId) => api.patch(`/agreements/${id}`, data, { params: { location_id: locationId } }),
  delete: (id, locationId) => api.delete(`/agreements/${id}`, { params: { location_id: locationId } }),
};

// Payments APIs
export const paymentsAPI = {
  list: (locationId, params = {}) => api.get('/payments', { params: { location_id: locationId, ...params } }),
  get: (id, locationId) => api.get(`/payments/${id}`, { params: { location_id: locationId } }),
  initiate: (data, locationId) => api.post('/payments/initiate', data, { params: { location_id: locationId } }),
  recordCash: (data, locationId) => api.post('/payments/cash', data, { params: { location_id: locationId } }),
  downloadInvoice: (paymentId, locationId) => api.get(`/payments/${paymentId}/invoice`, { params: { location_id: locationId }, responseType: 'blob' }),
  updateStatus: (paymentId, data, locationId) => api.patch(`/payments/${paymentId}/status`, data, { params: { location_id: locationId } }),
};

// Discipline Supervisor Requests
export const disciplineRequestsAPI = {
  create: (data, locationId) => api.post('/discipline/requests', data, { params: { ...(locationId ? { location_id: locationId } : {}) } }),
  list: (locationId, params = {}) => api.get('/discipline/requests', { params: { ...(locationId ? { location_id: locationId } : {}), ...params } }),
  my: (locationId, params = {}) => api.get('/discipline/requests/my', { params: { ...(locationId ? { location_id: locationId } : {}), ...params } }),
  act: (requestId, data, locationId) => api.patch(`/discipline/requests/${requestId}`, data, { params: { ...(locationId ? { location_id: locationId } : {}) } }),
  inbox: (locationId, params = {}) => api.get('/discipline/requests/inbox', { params: { ...(locationId ? { location_id: locationId } : {}), ...params } }),
  supervisorAct: (requestId, data, locationId) => api.patch(`/discipline/requests/${requestId}/supervisor`, data, { params: { ...(locationId ? { location_id: locationId } : {}) } }),
  performance: (locationId, params = {}) => api.get('/discipline/performance', { params: { ...(locationId ? { location_id: locationId } : {}), ...params } }),
};

// Coach Portal
export const coachAPI = {
  profile: () => api.get('/coach/profile'),
  disciplines: () => api.get('/coach/disciplines'),
  categories: (params = {}) => api.get('/coach/categories', { params }),
  supervisorPrograms: (params = {}) => api.get('/coach/supervisor/programs', { params }),
  supervisorSchedules: (params = {}) => api.get('/coach/supervisor/schedules', { params }),
  supervisorCoaches: (params = {}) => api.get('/coach/supervisor/coaches', { params }),
  schedule: (params = {}) => api.get('/coach/schedule', { params }),
  sessions: (params = {}) => api.get('/coach/sessions', { params }),
  sessionRoster: (sessionId) => api.get(`/coach/sessions/${sessionId}/roster`),
  saveAttendance: (data) => api.post('/coach/attendance', data),
  performance: (params = {}) => api.get('/coach/performance', { params }),
};

// Member Transfers APIs
export const transfersAPI = {
  list: (status = null) => api.get('/member-transfers', { params: status ? { status } : {} }),
  action: (id, data) => api.patch(`/member-transfers/${id}`, data),
};

// Dashboard Alerts API
export const alertsAPI = {
  get: (locationId) => api.get('/dashboard/alerts', { params: { location_id: locationId } }),
};

// Public Member Registration APIs (no auth)
export const publicMemberAPI = {
  getLocationInfo: (locationId) => api.get(`/public/location/${locationId}/info`),
  register: (data, locationId) => api.post('/public/register', data, { params: { location_id: locationId } }),
  getMemberships: (memberId, locationId) => api.get(`/public/members/${memberId}/memberships`, { params: { location_id: locationId } }),
};

// Sales Module APIs
export const salesAPI = {
  // Teams
  listTeams: (params = {}) => api.get('/sales/teams', { params }),
  getTeam: (id) => api.get(`/sales/teams/${id}`),
  createTeam: (data) => api.post('/sales/teams', data),
  updateTeam: (id, data) => api.patch(`/sales/teams/${id}`, data),
  deactivateTeam: (id) => api.patch(`/sales/teams/${id}/deactivate`),
  // Team Members
  listTeamMembers: (teamId, params = {}) => api.get(`/sales/teams/${teamId}/members`, { params }),
  addTeamMember: (teamId, data) => api.post(`/sales/teams/${teamId}/members`, data),
  updateTeamMember: (teamId, memberId, data) => api.patch(`/sales/teams/${teamId}/members/${memberId}`, data),
  deactivateTeamMember: (teamId, memberId) => api.patch(`/sales/teams/${teamId}/members/${memberId}/deactivate`),
  transferAgent: (memberId, data) => api.post(`/sales/members/${memberId}/transfer`, data),
  // Leads
  listLeads: (params = {}) => api.get('/sales/leads', { params }),
  getLead: (id) => api.get(`/sales/leads/${id}`),
  getLeadTimeline: (id, params = {}) => api.get(`/sales/leads/${id}/timeline`, { params }),
  createLead: (data) => api.post('/sales/leads', data),
  updateLead: (id, data) => api.patch(`/sales/leads/${id}`, data),
  updateLeadStatus: (id, data) => api.patch(`/sales/leads/${id}/status`, data),
  addLeadActivity: (id, data) => api.post(`/sales/leads/${id}/activities`, data),
  transferLead: (id, data) => api.post(`/sales/leads/${id}/transfer`, data),
  mergeLead: (targetLeadId, data) => api.post(`/sales/leads/${targetLeadId}/merge`, data),
  convertLeadToMember: (id, data) => api.post(`/sales/leads/${id}/convert-to-member`, data),
  bulkImportLeads: (data) => api.post('/sales/leads/bulk-import', data),
  identityCheck: (params = {}) => api.get('/sales/identity/check', { params }),
  listLeadAttachments: (id, params = {}) => api.get(`/sales/leads/${id}/attachments`, { params }),
  addLeadAttachment: (id, data) => api.post(`/sales/leads/${id}/attachments`, data),
  // Helpers
  getAvailableStaff: (params = {}) => api.get('/sales/available-staff', { params }),
  getDashboardStats: (params = {}) => api.get('/sales/dashboard/stats', { params }),
  getCrmDashboard: (params = {}) => api.get('/sales/crm/dashboard', { params }),
  getCrmConversions: (params = {}) => api.get('/sales/crm/conversions', { params }),
  getFollowUpInbox: (params = {}) => api.get('/sales/follow-ups/inbox', { params }),
  getSlaSettings: (params = {}) => api.get('/sales/sla-settings', { params }),
  updateSlaSettings: (data, params = {}) => api.patch('/sales/sla-settings', data, { params }),
  getMaqsamSettings: (params = {}) => api.get('/sales/maqsam/settings', { params }),
  updateMaqsamSettings: (data, params = {}) => api.patch('/sales/maqsam/settings', data, { params }),
  syncMaqsamAgents: (params = {}) => api.post('/sales/maqsam/sync', {}, { params }),
  listMaqsamAgents: (params = {}) => api.get('/sales/maqsam/agents', { params }),
  updateMaqsamAgentMapping: (identifier, data, params = {}) => api.patch(`/sales/maqsam/agents/${identifier}/mapping`, data, { params }),
  syncMaqsamContacts: (params = {}) => api.post('/sales/maqsam/contacts/sync', {}, { params }),
  listMaqsamContacts: (params = {}) => api.get('/sales/maqsam/contacts', { params }),
  searchMaqsamContacts: (params = {}) => api.get('/sales/maqsam/contacts/search', { params }),
  getMaqsamContact: (identifier, params = {}) => api.get(`/sales/maqsam/contacts/${identifier}`, { params }),
  createMaqsamContact: (data, params = {}) => api.post('/sales/maqsam/contacts', data, { params }),
  updateMaqsamContact: (identifier, data, params = {}) => api.patch(`/sales/maqsam/contacts/${identifier}`, data, { params }),
  deleteMaqsamContact: (identifier, params = {}) => api.delete(`/sales/maqsam/contacts/${identifier}`, { params }),
  linkMaqsamContact: (identifier, data, params = {}) => api.post(`/sales/maqsam/contacts/${identifier}/link`, data, { params }),
  unlinkMaqsamContact: (identifier, params = {}) => api.post(`/sales/maqsam/contacts/${identifier}/unlink`, {}, { params }),
  createLeadFromMaqsamContact: (identifier, data, params = {}) => api.post(`/sales/maqsam/contacts/${identifier}/create-lead`, data, { params }),
  listRoutingRules: () => api.get('/sales/routing-rules'),
  createRoutingRule: (data) => api.post('/sales/routing-rules', data),
  updateRoutingRule: (id, data) => api.patch(`/sales/routing-rules/${id}`, data),
  // Performance Metrics
  getPerformance: (params = {}) => api.get('/sales/performance', { params }),
  // Revenue Reporting
  getRevenue: (params = {}) => api.get('/sales/revenue', { params }),
  getRevenueSummary: () => api.get('/sales/revenue/summary'),
};

// Member Import APIs (Manager only)
export const memberImportAPI = {
  getTemplate: () => api.get('/manager/members/import/template'),
  preview: (formData) => api.post('/manager/members/import/preview', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  start: (formData, mode) => api.post(`/manager/members/import/start?mode=${mode}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getJob: (jobId) => api.get(`/manager/members/import/${jobId}`),
  getErrors: (jobId) => api.get(`/manager/members/import/${jobId}/errors`),
  listJobs: (limit = 20) => api.get('/manager/members/import/jobs/list', { params: { limit } }),
};

// Admin CMS APIs
export const cmsAPI = {
  // Site Settings
  getSiteSettings: () => api.get('/admin/site-settings'),
  updateSiteSettings: (data) => api.patch('/admin/site-settings', data),
  // Disciplines
  listDisciplines: (includeInactive = false) => api.get('/admin/disciplines', { params: { include_inactive: includeInactive } }),
  createDiscipline: (data) => api.post('/admin/disciplines', data),
  updateDiscipline: (id, data) => api.patch(`/admin/disciplines/${id}`, data),
  deleteDiscipline: (id, permanent = true) => api.delete(`/admin/disciplines/${id}`, { params: { permanent } }),
  // Pricing
  listPricing: (includeInactive = false) => api.get('/admin/pricing', { params: { include_inactive: includeInactive } }),
  createPricing: (data) => api.post('/admin/pricing', data),
  updatePricing: (id, data) => api.patch(`/admin/pricing/${id}`, data),
  deletePricing: (id, permanent = true) => api.delete(`/admin/pricing/${id}`, { params: { permanent } }),
  // Location Manager FAQs
  listLocationFaqs: (locationId, includeInactive = false) => api.get(`/admin/branches/${locationId}/faqs`, { params: { include_inactive: includeInactive } }),
  createLocationFaq: (locationId, data) => api.post(`/admin/branches/${locationId}/faqs`, data),
  updateLocationFaq: (locationId, id, data) => api.patch(`/admin/branches/${locationId}/faqs/${id}`, data),
  deleteLocationFaq: (locationId, id, permanent = false) => api.delete(`/admin/branches/${locationId}/faqs/${id}`, { params: { permanent } }),
  
  // Global FAQs 
  listFaqs: (includeInactive = false, locationId = null) => api.get('/admin/faqs', { params: { include_inactive: includeInactive, location_id: locationId } }),
  createFaq: (data) => api.post('/admin/faqs', data),
  updateFaq: (id, data) => api.patch(`/admin/faqs/${id}`, data),
  deleteFaq: (id, permanent = true) => api.delete(`/admin/faqs/${id}`, { params: { permanent } }),
  // Static Pages
  listPages: (includeInactive = false) => api.get('/admin/pages', { params: { include_inactive: includeInactive } }),
  createPage: (data) => api.post('/admin/pages', data),
  updatePage: (id, data) => api.patch(`/admin/pages/${id}`, data),
  deletePage: (id, permanent = true) => api.delete(`/admin/pages/${id}`, { params: { permanent } }),
  // Branch public settings
  updateBranchPublic: (id, data) => api.patch(`/admin/branches/${id}/public`, data),
  // Contact submissions
  listContactSubmissions: (status) => api.get('/admin/contact-submissions', { params: status ? { status } : {} }),
  updateContactStatus: (id, status) => api.patch(`/admin/contact-submissions/${id}`, null, { params: { status } }),
  // Seed CMS
  seedCMS: () => api.post('/admin/seed-cms'),
};

// SEO APIs
export const seoAPI = {
  list: () => api.get('/admin/seo'),
  updatePage: (data) => api.patch('/admin/seo/page', data),
  updateGlobal: (data) => api.patch('/admin/seo/global', data),
  getPublic: (pagePath) => api.get(`/public/seo/${pagePath}`),
};

// Blog APIs
export const blogsAPI = {
  list: (status) => api.get('/admin/blogs', { params: status ? { status } : {} }),
  create: (data) => api.post('/admin/blogs', data),
  update: (id, data) => api.patch(`/admin/blogs/${id}`, data),
  delete: (id) => api.delete(`/admin/blogs/${id}`),
  getPublic: (params) => api.get('/public/blogs', { params }),
  getPublicBySlug: (slug) => api.get(`/public/blogs/${slug}`),
};

// Email Settings APIs
export const emailAPI = {
  getSMTP: () => api.get('/admin/email/smtp'),
  updateSMTP: (data) => api.patch('/admin/email/smtp', data),
  testSMTP: () => api.post('/admin/email/test'),
  getVariables: () => api.get('/admin/email/variables'),
  listTemplates: () => api.get('/admin/email/templates'),
  createTemplate: (data) => api.post('/admin/email/templates', data),
  updateTemplate: (id, data) => api.patch(`/admin/email/templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/admin/email/templates/${id}`),
};

export default api;
